import { FormEvent, useMemo, useState } from "react";
import { ApiError } from "@/api/client";
import { createUser, deleteUser, getLibraries, getUsers, updateUser } from "@/api/endpoints";
import { useAuth } from "@/hooks/useAuth";
import { useApi } from "@/hooks/useApi";
import type { Library, User } from "@/types/api";

type UserRole = "admin" | "user";

const EMPTY_USERS: User[] = [];
const EMPTY_LIBRARIES: Library[] = [];

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const users = useApi(() => getUsers(), []);
  const libraries = useApi(() => getLibraries(), []);

  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<UserRole>("user");
  const [enabled, setEnabled] = useState(true);
  const [canDownload, setCanDownload] = useState(false);
  const [canRequest, setCanRequest] = useState(false);
  const [allowAllLibraries, setAllowAllLibraries] = useState(true);
  const [allowedLibraryIds, setAllowedLibraryIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  const [editingID, setEditingID] = useState<string | null>(null);
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [editRole, setEditRole] = useState<UserRole>("user");
  const [editEnabled, setEditEnabled] = useState(true);
  const [editCanDownload, setEditCanDownload] = useState(false);
  const [editCanRequest, setEditCanRequest] = useState(false);
  const [editAllowAllLibraries, setEditAllowAllLibraries] = useState(true);
  const [editAllowedLibraryIds, setEditAllowedLibraryIds] = useState<string[]>([]);
  const [updatingID, setUpdatingID] = useState<string | null>(null);
  const [removingID, setRemovingID] = useState<string | null>(null);
  const [togglingID, setTogglingID] = useState<string | null>(null);

  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const userList = users.data?.data ?? EMPTY_USERS;
  const libraryList = libraries.data?.data ?? EMPTY_LIBRARIES;
  const libraryById = useMemo(() => {
    return new Map(libraryList.map((library) => [library.id, library]));
  }, [libraryList]);

  const enabledUserCount = userList.filter((target) => target.attributes.is_enabled).length;
  const adminCount = userList.filter((target) => target.attributes.is_admin).length;
  const restrictedUserCount = userList.filter((target) => !userHasAllLibraries(target)).length;
  const activeSessionCount = userList.reduce((total, target) => total + (target.attributes.active_session_count ?? 0), 0);

  const lastEnabledAdminId = useMemo(() => {
    const enabledAdmins = userList.filter((target) => target.attributes.is_admin && target.attributes.is_enabled);
    return enabledAdmins.length === 1 ? enabledAdmins[0]?.id ?? null : null;
  }, [userList]);

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCreating(true);
    setMessage(null);
    setError(null);
    try {
      await createUser({
        username: username.trim(),
        display_name: displayName.trim(),
        password: password,
        is_admin: role === "admin",
        is_enabled: enabled,
        can_download: canDownload,
        can_request: canRequest,
        all_libraries: allowAllLibraries,
        allowed_library_ids: allowAllLibraries ? [] : allowedLibraryIds,
      });
      setUsername("");
      setDisplayName("");
      setPassword("");
      setRole("user");
      setEnabled(true);
      setCanDownload(false);
      setCanRequest(false);
      setAllowAllLibraries(true);
      setAllowedLibraryIds([]);
      setMessage("User added");
      users.refetch();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem.detail);
      } else {
        setError("Failed to add user");
      }
    } finally {
      setCreating(false);
    }
  }

  function beginEdit(target: User) {
    setEditingID(target.id);
    setEditDisplayName(target.attributes.display_name);
    setEditPassword("");
    setEditRole(target.attributes.is_admin ? "admin" : "user");
    setEditEnabled(target.attributes.is_enabled);
    setEditCanDownload(target.attributes.can_download);
    setEditCanRequest(target.attributes.can_request);
    setEditAllowAllLibraries(userHasAllLibraries(target));
    setEditAllowedLibraryIds(target.attributes.allowed_library_ids ?? []);
    setMessage(null);
    setError(null);
  }

  function cancelEdit() {
    setEditingID(null);
    setEditDisplayName("");
    setEditPassword("");
    setEditRole("user");
    setEditEnabled(true);
    setEditCanDownload(false);
    setEditCanRequest(false);
    setEditAllowAllLibraries(true);
    setEditAllowedLibraryIds([]);
  }

  async function handleUpdate(userId: string) {
    setUpdatingID(userId);
    setMessage(null);
    setError(null);
    try {
      await updateUser(userId, {
        display_name: editDisplayName.trim(),
        is_admin: editRole === "admin",
        is_enabled: editEnabled,
        can_download: editCanDownload,
        can_request: editCanRequest,
        all_libraries: editAllowAllLibraries,
        allowed_library_ids: editAllowAllLibraries ? [] : editAllowedLibraryIds,
        ...(editPassword.trim() ? { password: editPassword } : {}),
      });
      setMessage("User updated");
      cancelEdit();
      users.refetch();
      if (currentUser?.id === userId) {
        window.location.reload();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem.detail);
      } else {
        setError("Failed to update user");
      }
    } finally {
      setUpdatingID(null);
    }
  }

  async function handleToggle(target: User) {
    const nextEnabled = !target.attributes.is_enabled;
    setTogglingID(target.id);
    setMessage(null);
    setError(null);
    try {
      await updateUser(target.id, { is_enabled: nextEnabled });
      setMessage(nextEnabled ? "User enabled" : "User disabled");
      users.refetch();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem.detail);
      } else {
        setError(nextEnabled ? "Failed to enable user" : "Failed to disable user");
      }
    } finally {
      setTogglingID(null);
    }
  }

  async function handleRemove(target: User) {
    if (!window.confirm(`Remove user "${target.attributes.username}"?`)) {
      return;
    }
    setRemovingID(target.id);
    setMessage(null);
    setError(null);
    try {
      await deleteUser(target.id);
      setMessage("User removed");
      users.refetch();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem.detail);
      } else {
        setError("Failed to remove user");
      }
    } finally {
      setRemovingID(null);
    }
  }

  if (users.loading) {
    return (
      <div className="admin-page users-admin-page">
        <div className="users-page-state">Loading users...</div>
      </div>
    );
  }

  if (users.error) {
    return (
      <div className="admin-page users-admin-page">
        <p className="error-message">Failed to load users</p>
      </div>
    );
  }

  return (
    <div className="admin-page users-admin-page">
      <header className="users-page-header">
        <div className="users-page-title">
          <h1>Users</h1>
          <p className="settings-copy">
            Manage sign-in, account status, permissions, and library access.
          </p>
        </div>
        <div className="users-summary-grid" aria-label="User summary">
          <UserSummary label="Total" value={userList.length} />
          <UserSummary label="Enabled" value={enabledUserCount} />
          <UserSummary label="Admins" value={adminCount} />
          <UserSummary label="Restricted" value={restrictedUserCount} />
          <UserSummary label="Sessions" value={activeSessionCount} />
        </div>
      </header>

      <section className="settings-section users-create-section" aria-labelledby="users-add-heading">
        <div className="users-section-heading">
          <h2 id="users-add-heading">Add User</h2>
        </div>
        <form className="settings-form users-form" onSubmit={handleCreate}>
          <div className="users-form-grid">
            <label className="settings-field">
              <span>Username</span>
              <input
                className="settings-input"
                type="text"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
                required
              />
            </label>
            <label className="settings-field">
              <span>Display Name</span>
              <input
                className="settings-input"
                type="text"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                autoComplete="name"
              />
            </label>
            <label className="settings-field">
              <span>Password</span>
              <input
                className="settings-input"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            <label className="settings-field">
              <span>Role</span>
              <select
                className="settings-select"
                value={role}
                onChange={(event) => setRole(event.target.value as UserRole)}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </label>
          </div>

          <fieldset className="users-option-group">
            <legend>Account Options</legend>
            <div className="users-checkbox-grid">
              <label className="settings-checkbox settings-checkbox--compact">
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(event) => setEnabled(event.target.checked)}
                />
                <span>Enabled</span>
              </label>
              <label className="settings-checkbox settings-checkbox--compact">
                <input
                  type="checkbox"
                  checked={canDownload}
                  onChange={(event) => setCanDownload(event.target.checked)}
                />
                <span>Allow offline downloads</span>
              </label>
              <label className="settings-checkbox settings-checkbox--compact">
                <input
                  type="checkbox"
                  checked={canRequest}
                  onChange={(event) => setCanRequest(event.target.checked)}
                />
                <span>Allow content requests</span>
              </label>
            </div>
          </fieldset>

          <LibraryRestrictionPicker
            libraries={libraryList}
            allowAll={allowAllLibraries}
            selectedIds={allowedLibraryIds}
            onAllowAllChange={setAllowAllLibraries}
            onChange={setAllowedLibraryIds}
          />

          <div className="settings-actions settings-actions--end">
            <button className="btn btn--primary" type="submit" disabled={creating}>
              {creating ? "Adding..." : "Add User"}
            </button>
          </div>
        </form>
      </section>

      {(message || error) && (
        <div className="users-status-row">
          {message && <p className="settings-status settings-status--success">{message}</p>}
          {error && <p className="settings-status settings-status--error">{error}</p>}
        </div>
      )}

      <section className="users-accounts-section" aria-labelledby="users-accounts-heading">
        <div className="users-section-heading users-section-heading--split">
          <div>
            <h2 id="users-accounts-heading">Accounts</h2>
            <p className="settings-copy">{enabledUserCount} enabled of {userList.length} total</p>
          </div>
        </div>

        <div className="users-account-list">
          {userList.length === 0 && (
            <div className="users-empty-state">No users yet.</div>
          )}
          {userList.map((target) => {
            const isCurrentUser = currentUser?.id === target.id;
            const isLastEnabledAdmin = lastEnabledAdminId === target.id;
            const roleLabel = target.attributes.is_admin ? "Admin" : "User";
            const statusLabel = target.attributes.is_enabled ? "Enabled" : "Disabled";
            const selectedLibraryIds = target.attributes.allowed_library_ids ?? [];
            const libraryAccessLabel = formatLibraryAccess(userHasAllLibraries(target), selectedLibraryIds, libraryById);
            const permissionLabels = formatPermissionLabels(target);
            const userActiveSessionCount = target.attributes.active_session_count ?? 0;
            const activeDevices = target.attributes.active_devices ?? [];

            return (
              <article
                key={target.id}
                className={`user-account-card${target.attributes.is_enabled ? "" : " user-account-card--disabled"}`}
                aria-label={`${target.attributes.display_name} account`}
              >
                {editingID === target.id ? (
                  <form
                    className="user-account-edit-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void handleUpdate(target.id);
                    }}
                  >
                    <div className="user-account-edit-header">
                      <div className="user-account-avatar" aria-hidden="true">
                        {initialsForUser(target)}
                      </div>
                      <div>
                        <h3>{target.attributes.display_name}</h3>
                        <div className="user-account-username">@{target.attributes.username}</div>
                      </div>
                    </div>

                    <div className="users-form-grid users-form-grid--edit">
                      <label className="settings-field">
                        <span>Display Name</span>
                        <input
                          className="settings-input"
                          type="text"
                          value={editDisplayName}
                          onChange={(event) => setEditDisplayName(event.target.value)}
                        />
                      </label>
                      <label className="settings-field">
                        <span>Role</span>
                        <select
                          className="settings-select"
                          value={editRole}
                          onChange={(event) => setEditRole(event.target.value as UserRole)}
                          disabled={isLastEnabledAdmin}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </label>
                      <label className="settings-field">
                        <span>Password</span>
                        <input
                          className="settings-input"
                          type="password"
                          value={editPassword}
                          onChange={(event) => setEditPassword(event.target.value)}
                          placeholder="Leave blank to keep current password"
                          autoComplete="new-password"
                        />
                      </label>
                      <label className="settings-field">
                        <span>Status</span>
                        <select
                          className="settings-select"
                          value={editEnabled ? "enabled" : "disabled"}
                          onChange={(event) => setEditEnabled(event.target.value === "enabled")}
                          disabled={isCurrentUser || isLastEnabledAdmin}
                        >
                          <option value="enabled">Enabled</option>
                          <option value="disabled">Disabled</option>
                        </select>
                      </label>
                    </div>

                    <fieldset className="users-option-group">
                      <legend>Account Options</legend>
                      <div className="users-checkbox-grid">
                        <label className="settings-checkbox settings-checkbox--compact">
                          <input
                            type="checkbox"
                            checked={editCanDownload}
                            onChange={(event) => setEditCanDownload(event.target.checked)}
                          />
                          <span>Allow offline downloads</span>
                        </label>
                        <label className="settings-checkbox settings-checkbox--compact">
                          <input
                            type="checkbox"
                            checked={editCanRequest}
                            onChange={(event) => setEditCanRequest(event.target.checked)}
                          />
                          <span>Allow content requests</span>
                        </label>
                      </div>
                    </fieldset>

                    <LibraryRestrictionPicker
                      libraries={libraryList}
                      allowAll={editAllowAllLibraries}
                      selectedIds={editAllowedLibraryIds}
                      onAllowAllChange={setEditAllowAllLibraries}
                      onChange={setEditAllowedLibraryIds}
                    />

                    <div className="settings-actions settings-actions--end">
                      <button
                        className="btn btn--primary"
                        type="submit"
                        disabled={updatingID === target.id}
                      >
                        {updatingID === target.id ? "Saving..." : "Save Changes"}
                      </button>
                      <button className="btn btn--secondary" type="button" onClick={cancelEdit}>
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="user-account-view">
                    <div className="user-account-main">
                      <div className="user-account-avatar" aria-hidden="true">
                        {initialsForUser(target)}
                      </div>
                      <div className="user-account-content">
                        <div className="user-account-title-row">
                          <div>
                            <h3>{target.attributes.display_name}</h3>
                            <div className="user-account-username">@{target.attributes.username}</div>
                          </div>
                          <div className="user-account-badges">
                            <span className={`badge user-role-badge user-role-badge--${target.attributes.is_admin ? "admin" : "user"}`}>
                              {roleLabel}
                            </span>
                            <span className={`badge ${target.attributes.is_enabled ? "badge--success" : "badge--muted"}`}>
                              {statusLabel}
                            </span>
                            {isLastEnabledAdmin && <span className="badge badge--muted">Protected</span>}
                            {isCurrentUser && <span className="badge badge--muted">Current</span>}
                            {target.attributes.sso_linked && (
                              <span className="badge badge--muted" title={target.attributes.has_password === false ? "Signs in through single sign-on; no password set" : "Linked to single sign-on"}>
                                SSO
                              </span>
                            )}
                          </div>
                        </div>

                        <dl className="user-account-details">
                          <div>
                            <dt>Library Access</dt>
                            <dd>{libraryAccessLabel}</dd>
                          </div>
                          <div>
                            <dt>Permissions</dt>
                            <dd>{permissionLabels.join(", ")}</dd>
                          </div>
                          <div>
                            <dt>Active Sessions</dt>
                            <dd>{formatActiveSessions(userActiveSessionCount, activeDevices)}</dd>
                          </div>
                          <div>
                            <dt>Last Seen</dt>
                            <dd>{formatLastSeenAt(target.attributes.last_seen_at)}</dd>
                          </div>
                          <div>
                            <dt>Created</dt>
                            <dd>{formatCreatedAt(target.attributes.created_at)}</dd>
                          </div>
                        </dl>
                      </div>
                    </div>

                    <div className="user-account-actions">
                      <button className="btn btn--secondary btn--compact" type="button" onClick={() => beginEdit(target)}>
                        Edit
                      </button>
                      <button
                        className="btn btn--secondary btn--compact"
                        type="button"
                        disabled={togglingID === target.id || isCurrentUser || isLastEnabledAdmin}
                        onClick={() => void handleToggle(target)}
                      >
                        {togglingID === target.id
                          ? (target.attributes.is_enabled ? "Disabling..." : "Enabling...")
                          : (target.attributes.is_enabled ? "Disable" : "Enable")}
                      </button>
                      <button
                        className="btn btn--secondary btn--compact user-danger-button"
                        type="button"
                        disabled={removingID === target.id || isCurrentUser || isLastEnabledAdmin}
                        onClick={() => void handleRemove(target)}
                      >
                        {removingID === target.id ? "Removing..." : "Remove"}
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function UserSummary({ label, value }: { label: string; value: number }) {
  return (
    <div className="users-summary-item">
      <div className="users-summary-value">{value}</div>
      <div className="users-summary-label">{label}</div>
    </div>
  );
}

function LibraryRestrictionPicker({
  libraries,
  allowAll,
  selectedIds,
  onAllowAllChange,
  onChange,
}: {
  libraries: Library[];
  allowAll: boolean;
  selectedIds: string[];
  onAllowAllChange: (value: boolean) => void;
  onChange: (value: string[]) => void;
}) {
  function toggle(libraryId: string) {
    if (selectedIds.includes(libraryId)) {
      onChange(selectedIds.filter((id) => id !== libraryId));
    } else {
      onChange([...selectedIds, libraryId]);
    }
  }

  return (
    <fieldset className="settings-library-restrictions users-library-restrictions">
      <legend>Library Access</legend>
      <label className="settings-checkbox">
        <input type="checkbox" checked={allowAll} onChange={(event) => onAllowAllChange(event.target.checked)} />
        <span>All libraries (including ones added later)</span>
      </label>
      {!allowAll && (
        <>
          <div className="users-library-restrictions-summary">
            <span>
              {selectedIds.length === 0 ? "No access — select libraries below" : `${selectedIds.length} selected`}
            </span>
          </div>
          {libraries.length === 0 ? (
            <p className="users-library-empty">No libraries configured.</p>
          ) : (
            <div className="settings-library-restriction-grid">
              {libraries.map((library) => (
                <label key={library.id} className="settings-checkbox settings-checkbox--compact">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(library.id)}
                    onChange={() => toggle(library.id)}
                  />
                  <span>{library.attributes.name}</span>
                </label>
              ))}
            </div>
          )}
        </>
      )}
    </fieldset>
  );
}

function userHasAllLibraries(user: User): boolean {
  return user.attributes.all_libraries ?? (user.attributes.allowed_library_ids?.length ?? 0) === 0;
}

function formatLibraryAccess(allowAll: boolean, selectedIds: string[], librariesById: Map<string, Library>) {
  if (allowAll) {
    return "All libraries";
  }
  if (selectedIds.length === 0) {
    return "No access";
  }

  const names = selectedIds.map((id) => librariesById.get(id)?.attributes.name).filter((name): name is string => Boolean(name));
  if (names.length === 0) {
    return `${selectedIds.length} libraries allowed`;
  }

  if (names.length <= 2) {
    return names.join(", ");
  }

  return `${names.slice(0, 2).join(", ")} + ${names.length - 2} more`;
}

function formatPermissionLabels(user: User) {
  const labels: string[] = [];
  if (user.attributes.can_download) {
    labels.push("Downloads");
  }
  if (user.attributes.can_request) {
    labels.push("Requests");
  }
  return labels.length > 0 ? labels : ["Streaming"];
}

function formatCreatedAt(value: string) {
  const created = new Date(value);
  if (Number.isNaN(created.getTime())) {
    return "Unknown";
  }
  return created.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatActiveSessions(count: number, devices: string[]) {
  if (count <= 0) {
    return "None";
  }

  const sessionLabel = count === 1 ? "1 active session" : `${count} active sessions`;
  const deviceNames = devices.map((device) => device.trim()).filter(Boolean);
  if (deviceNames.length === 0) {
    return sessionLabel;
  }

  const visibleDevices = deviceNames.slice(0, 3);
  const remainingCount = deviceNames.length - visibleDevices.length;
  const deviceLabel = remainingCount > 0
    ? `${visibleDevices.join(", ")} + ${remainingCount} more`
    : visibleDevices.join(", ");
  return `${sessionLabel} on ${deviceLabel}`;
}

function formatLastSeenAt(value?: string | null) {
  if (!value) {
    return "Never";
  }
  const seenAt = new Date(value);
  if (Number.isNaN(seenAt.getTime())) {
    return "Unknown";
  }
  return seenAt.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function initialsForUser(user: User) {
  const source = user.attributes.display_name.trim() || user.attributes.username.trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  const first = parts[0] ?? "";
  if (parts.length === 1) {
    return first.slice(0, 2).toUpperCase();
  }
  const second = parts[1] ?? "";
  return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
}
